import { expVal } from '@confluence/feature-experiments';
import { preloadEditorData } from '@confluence/load-edit-page/entry-points/preloadEditorData';
import { preloadEditorOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadEditorOnboardingState';
import { preloadLandUsersInEditorWithRevampedBpqa } from '@confluence/onboarding-helpers/entry-points/preloadLandUsersInEditorWithRevampedBpqa';
import { preloadNthUserEmojiTitleDiscovered } from '@confluence/onboarding-helpers/entry-points/preloadNthUserEmojiTitleDiscovered';
import { preloadNthUserOnboardingCompletedTimestamp } from '@confluence/onboarding-helpers/entry-points/preloadNthUserOnboardingCompletedTimestamp';
import { preloadSpaceDetail } from '@confluence/space-utils/entry-points';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext';
import type { RouteMatch } from '@confluence/route/entry-points/Route';
import { preloadDraft } from '@confluence/fabric-providers/entry-points/preloadDraft';
import { preloadEditorByLineData } from '@confluence/content-header/entry-points/preloadEditorByLineData';
import { preloadContentTypesHeader } from '@confluence/content-types-header/entry-points/preloadContentTypesHeader';
import { preloadClassificationContextForEditContent } from '@confluence/data-classification/entry-points/queries';
import { preloadMediaToken } from '@confluence/fabric-media-support/entry-points/preloadMediaToken';
import { preloadPageRestrictionsQuery } from '@confluence/page-restrictions-context/entry-points/preloadPageRestrictionsQuery';
import { preloadRequestViewQuery } from '@confluence/no-permission/entry-points/preloadRequestViewQuery';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery';
import { fg } from '@confluence/feature-gating';
import { preloadSmartCreateFeedbackBanner } from '@confluence/editor-action-bar/entry-points/preloadSmartCreateFeedbackBanner';
import {
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations';
import { preloadWebPanelLocation } from '@confluence/web-panel-location/entry-points/preloadWebPanelLocation';
import { preloadRecordLoomButton } from '@confluence/loom-utils/entry-points/preloadRecordLoomButton';
import { preloadContentSmartLinks } from '@confluence/content-smartlinks/entry-points/preloadContentSmartLinks';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery';
import { FORGE_MODULE_XEN_MACRO } from '@confluence/forge-ui/entry-points/ForgeModuleType';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList';
import { getIsEmbeddedConfluence } from '@confluence/query-preloaders-common/entry-points/matchHelpers';
import { matchEditBlog } from '@confluence/query-preloaders-common/entry-points/matchRoutes';
import { preloadNavigationTasks } from '@confluence/query-preloaders-common/entry-points/preloadNavigationTasks';

export const preloadEditPageRoute = async (
	match: RouteMatch,
	_url: string,
	isTransition: boolean = false,
) => {
	const { userId, isLicensed, cloudId, accessStatus, isLoggedIn, environment } =
		getPreloaderFnContext();
	const tasks: Promise<any>[] = [];
	const { contentId, spaceKey } = match.params;

	const isBlog = Boolean(matchEditBlog(_url));

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const shouldPreloadMentionsProvider = Boolean(process.env.REACT_SSR);
	const isEmbeddedConfluence = getIsEmbeddedConfluence(match);
	const shouldPreloadNavigation = !isEmbeddedConfluence;

	tasks.push(
		preloadEditorData({
			spaceKey,
			contentId,
			cloudId,
			shouldPreloadMentionsProvider,
			useCache: false,
			isBlog,
		}),
	);

	// we dont know if a page is unpublished until we get data back. guess based on the draftShareId being in the url
	const guessIsUnpublishedDraft = Boolean(match?.query?.draftShareId);
	tasks.push(
		preloadEditorByLineData({
			isUnpublishedDraft: guessIsUnpublishedDraft,
			spaceKey,
			contentId,
		}),
	);

	// When `platform_editor_smart_card_otp` is enabled, we don't need to preload the smart links,
	// because it will be handled by the editor's one-tick providers.
	if (!expVal('platform_editor_smart_card_otp', 'isEnabled', false)) {
		const contentIdWithAri = `ari:cloud:confluence:${cloudId}:page/${contentId}`;
		tasks.push(preloadContentSmartLinks(contentIdWithAri, true));
	}

	if (fg('confluence_frontend_content_wrapper')) {
		tasks.push(preloadContentTypesHeader(contentId, fg('confluence_frontend_content_wrapper')));
		// Standalone Restrictions button needs to have its data preloaded
		tasks.push(preloadPageRestrictionsQuery(contentId, false));
	}

	// Unified Share and Restrict Button
	if (fg('confluence_frontend_usd_ssr')) {
		tasks.push(preloadShareAndRestrictButtonQuery(contentId));
	}

	if (isLicensed) {
		tasks.push(preloadEditorOnboardingState());
		tasks.push(preloadLandUsersInEditorWithRevampedBpqa());
		if (fg('auto_page_emoji_kill_switch')) {
			tasks.push(preloadNthUserOnboardingCompletedTimestamp());
			tasks.push(preloadNthUserEmojiTitleDiscovered());
		}
	}

	if (isTransition) {
		return waitForAllReturnWhenError(tasks);
	}

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		// Navigation and page tree
		if (shouldPreloadNavigation) {
			tasks.push(preloadNavigationTasks(spaceKey, contentId, isLicensed, isBlog));
		}

		tasks.push(preloadMediaToken(contentId), preloadClassificationContextForEditContent(contentId));
	} else {
		tasks.push(preloadDraft(contentId));
	}

	tasks.push(preloadSpaceDetail(spaceKey));

	tasks.push(
		preloadWebPanelLocation({ contentId, location: ATL_GENERAL }),
		preloadWebPanelLocation({ contentId, location: ATL_FOOTER }),
	);

	tasks.push(preloadRequestViewQuery(contentId));

	// Preload Loom button in Action Bar
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS) &&
		isLoggedIn
	) {
		tasks.push(
			preloadRecordLoomButton({
				cloudId,
				accessStatus,
				isLoggedIn,
				userId,
				environment,
			}),
		);
	}

	// Preload forge extension manifest
	if (fg('confluence_preload_forge_viewport_heights_editor')) {
		tasks.push(
			preloadUseExtensionList({
				contentId,
				spaceKey,
				moduleType: FORGE_MODULE_XEN_MACRO,
			}),
		);
	}

	// Preload Smart Create Feedback Banner
	if (fg('aifc_create_enabled')) {
		tasks.push(preloadSmartCreateFeedbackBanner(contentId));
	}

	return waitForAllReturnWhenError(tasks);
};
